import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { UADetect as UADetectClass, uaDetectToken } from 'go-modules/detect/ua-detect.service';
import type { GoToastOptions } from '@go-modules/interfaces/go-toast-options';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { focusElementWithFallback } from '@go-modules/utilities';
import { TranslateService } from '@ngx-translate/core';
import { Subject, take, takeUntil } from 'rxjs';

const OPEN_MODAL_SELECTOR = '.modal.in, .modal.show, .overlay-panel-modal, .mat-dialog-container, .mat-mdc-dialog-container';
const FOCUS_RETRY_DELAY = 50;
const FOCUS_RETRY_LIMIT = 10;

@Component({
	selector: 'ngx-go-toast',
	templateUrl: './go-toast.component.html',
	styleUrls: ['./go-toast.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NgxGoToastComponent implements OnInit, OnDestroy {

	public toastDetails: GoToastOptions;
	public toastAnnouncementDetails: GoToastOptions | null = null;
	public toastAnnouncementMessage: string | null = null;
	public shouldHideVisibleToastFromAssistiveTech = false;
	public ToastStatusTypes = GoToastStatusType;
	public hideToastTime: null|number;
	public readonly HIDE_TOAST_TIME = 5000;

	private toastTimeout: ReturnType<typeof setTimeout> | null = null;
	private focusToastTimeout: ReturnType<typeof setTimeout> | null = null;
	private destroyed$ = new Subject();

	constructor (
		@Inject(uaDetectToken) public UADetect: UADetectClass,
		@Inject(DOCUMENT) private documentRef: Document,
		private elementRef: ElementRef<HTMLElement>,
		private cdr: ChangeDetectorRef,
		private goToastService: NgxGoToastService,
		private translate: TranslateService
	) {}

	public ngOnInit () {
		this.goToastService.toastObserver.pipe(takeUntil(this.destroyed$)).subscribe((toastOptions: GoToastOptions) => {
			this.setToastDetails(toastOptions);
		});
	}

	public ngOnDestroy () {
		this.destroyed$.next(null);
		this.destroyed$.complete();
		this.clearToastTimeout();
		this.clearFocusToastTimeout();
	}

	public shouldShowToast () {
		return !!this.toastDetails;
	}

	public undoAction () {
		this.toastDetails.undo();
		this.closeToast();
	}

	public closeToast (): void {
		this.clearToastTimeout();
		this.toastDetails = null;
		this.resetToastAnnouncement();
		this.cdr.detectChanges();
	}

	private setToastDetails (data: GoToastOptions): void {
		this.clearToastTimeout();
		this.hideToastTime = data.hideToastTime ?? this.HIDE_TOAST_TIME;

		this.toastDetails = data;
		this.resetToastAnnouncement();
		this.shouldHideVisibleToastFromAssistiveTech = this.hasOpenModal();
		this.cdr.detectChanges();
		this.focusToast();

		this.toastTimeout = setTimeout(() => {
			this.closeToast();
		}, this.hideToastTime);
	}

	private clearToastTimeout (): void {
		if (this.toastTimeout == null) {
			return;
		}

		clearTimeout(this.toastTimeout);
		this.toastTimeout = null;
	}

	private focusToast (attempt = 0): void {
		if (this.hasOpenModal()) {
			this.shouldHideVisibleToastFromAssistiveTech = true;
			this.cdr.detectChanges();
			this.retryFocusToast(attempt);
			return;
		}

		this.resetToastAnnouncement(false);
		this.cdr.detectChanges();
		const toast = this.getToastElement();
		focusElementWithFallback(toast);
	}

	private retryFocusToast (attempt: number): void {
		if (attempt >= FOCUS_RETRY_LIMIT || this.toastDetails == null) {
			this.announceToast();
			return;
		}

		this.focusToastTimeout = setTimeout(() => {
			this.focusToastTimeout = null;
			this.focusToast(attempt + 1);
		}, FOCUS_RETRY_DELAY);
	}

	private announceToast (): void {
		const toastDetails = this.toastDetails;
		if (toastDetails == null) {
			this.resetToastAnnouncement(false);
			this.cdr.detectChanges();
			return;
		}

		this.shouldHideVisibleToastFromAssistiveTech = true;
		this.cdr.detectChanges();

		this.translate.get(toastDetails.message, toastDetails.messageParams)
			.pipe(take(1), takeUntil(this.destroyed$)).subscribe((message) => {
				if (this.toastDetails !== toastDetails) {
					return;
				}

				this.toastAnnouncementDetails = toastDetails;
				this.toastAnnouncementMessage = message;
				this.cdr.detectChanges();
			});
	}

	private resetToastAnnouncement (shouldClearTimeout: boolean = true): void {
		this.toastAnnouncementDetails = null;
		this.toastAnnouncementMessage = null;
		this.shouldHideVisibleToastFromAssistiveTech = false;

		if (shouldClearTimeout) {
			this.clearFocusToastTimeout();
		}
	}

	private clearFocusToastTimeout (): void {
		if (this.focusToastTimeout == null) {
			return;
		}

		clearTimeout(this.focusToastTimeout);
		this.focusToastTimeout = null;
	}

	private hasOpenModal (): boolean {
		return this.documentRef.querySelector(OPEN_MODAL_SELECTOR) != null;
	}

	private getToastElement (): HTMLElement | null {
		const toast = this.elementRef.nativeElement.querySelector('.toast-message');
		return toast instanceof HTMLElement ? toast : null;
	}
}
